import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { DATA_GRID_EMPTY_STATE_NO_DATA_LABEL } from '@/ui/primitives';
import type {
  DataGridProps,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
  GridSortModel,
  GridValidRowModel,
} from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives';
import type { Dispatch, SetStateAction } from 'react';

export type BuildServerDataTableGridPropsArgs<T extends GridValidRowModel> = {
  autoHeight: boolean;
  captureScrollAnchor: () => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  columns: ReadonlyArray<GridColDef<T>>;
  dataGridSx?: SxProps<Theme>;
  defaultRowHeight?: number;
  disableColumnMenu: boolean;
  disableColumnResize?: boolean;
  disableColumnSorting?: boolean;
  disablePageSizeSelect?: boolean;
  emptyOverlayHeight?: number;
  effectiveErrorMessage: string | null;
  effectiveRowCount: number;
  effectiveRowsData: T[];
  externalLoading?: boolean;
  filterModel: GridFilterModel;
  getRowHeight?: DataGridProps['getRowHeight'];
  getRowId: (row: T) => GridRowId;
  gridProps?: Partial<DataGridProps>;
  handleColumnWidthChange: NonNullable<DataGridProps['onColumnWidthChange']>;
  handleFilterModelChange: (model: GridFilterModel) => void;
  handlePaginationModelChange: (model: GridPaginationModel) => void;
  handleSortModelChange: (model: GridSortModel) => void;
  hideFooter?: boolean;
  hidePaginationControls?: boolean;
  onCellClick?: (params: GridCellParams<T>) => void;
  onColumnHeaderClick?: DataGridProps['onColumnHeaderClick'];
  onRowClick?: (params: GridRowParams<T>) => void;
  paginationModel: GridPaginationModel;
  queryIsFetching?: boolean;
  resolvedNoRowsText: string;
  resolvedPageSizeOptions: number[];
  setColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  showCellVerticalBorder?: boolean;
  showColumnVerticalBorder?: boolean;
  showToolbar: boolean;
  sortModel: GridSortModel;
  suppressFocusOutline?: boolean;
};

const zeroRowSpacing: NonNullable<DataGridProps['getRowSpacing']> = () => ({
  top: 0,
  bottom: 0,
});

const resolveGridRowHeight = (
  gridRowHeight: number | undefined,
  defaultRowHeight: number,
): number => {
  if (gridRowHeight === undefined) {
    return defaultRowHeight;
  }

  if (gridRowHeight === TABLE_ROW_HEIGHT || gridRowHeight === TABLE_ROW_MEASURE_HEIGHT) {
    return defaultRowHeight;
  }

  return gridRowHeight;
};

const isColumnVisibilityModelEqual = (
  left: GridColumnVisibilityModel,
  right: GridColumnVisibilityModel,
): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
};

export const buildServerDataTableGridProps = <T extends GridValidRowModel>({
  autoHeight,
  captureScrollAnchor,
  columnVisibilityModel,
  columns,
  dataGridSx,
  defaultRowHeight,
  disableColumnMenu,
  disableColumnResize,
  disableColumnSorting,
  disablePageSizeSelect,
  emptyOverlayHeight,
  effectiveErrorMessage,
  effectiveRowCount,
  effectiveRowsData,
  externalLoading: _externalLoading,
  filterModel,
  getRowHeight,
  getRowId,
  gridProps,
  handleColumnWidthChange,
  handleFilterModelChange,
  handlePaginationModelChange,
  handleSortModelChange,
  hideFooter,
  hidePaginationControls,
  onCellClick,
  onColumnHeaderClick,
  onRowClick,
  paginationModel,
  queryIsFetching: _queryIsFetching,
  resolvedNoRowsText: _resolvedNoRowsText,
  resolvedPageSizeOptions,
  setColumnVisibilityModel,
  showCellVerticalBorder,
  showColumnVerticalBorder,
  showToolbar,
  sortModel,
  suppressFocusOutline,
}: BuildServerDataTableGridPropsArgs<T>): DataGridProps => {
  const resolvedDisableColumnResize = disableColumnResize ?? false;
  const resolvedDisableColumnSorting = disableColumnSorting ?? true;
  const resolvedDisablePageSizeSelect = disablePageSizeSelect ?? false;
  const resolvedHideFooter = hideFooter ?? false;
  const resolvedSuppressFocusOutline = suppressFocusOutline ?? false;
  const sortingProps: Partial<DataGridProps> = resolvedDisableColumnSorting
    ? {}
    : {
        sortingMode: 'server',
        sortModel,
        onSortModelChange: handleSortModelChange,
      };
  const resolvedDataGridSx: SxProps<Theme> = [
    ...(dataGridSx ? (Array.isArray(dataGridSx) ? dataGridSx : [dataGridSx]) : []),
    resolvedHideFooter
      ? {
          '& .MuiDataGrid-main': {
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          },
        }
      : null,
  ].filter(Boolean) as SxProps<Theme>;
  const {
    columnHeaderHeight: gridColumnHeaderHeight,
    getEstimatedRowHeight: gridGetEstimatedRowHeight,
    getRowHeight: gridGetRowHeight,
    onColumnVisibilityModelChange: gridOnColumnVisibilityModelChange,
    rowHeight: gridRowHeight,
    slotProps: gridSlotProps = {},
    ...restGridProps
  }: Partial<DataGridProps> = gridProps ?? {};
  const handleColumnHeaderClick: NonNullable<DataGridProps['onColumnHeaderClick']> = (
    params,
    event,
    details,
  ) => {
    captureScrollAnchor();
    onColumnHeaderClick?.(params, event, details);
  };
  const resolvedGetRowHeight = gridGetRowHeight ?? getRowHeight;
  const resolvedRowHeight =
    resolvedGetRowHeight === undefined
      ? resolveGridRowHeight(gridRowHeight, defaultRowHeight ?? TABLE_ROW_MEASURE_HEIGHT)
      : undefined;

  return {
    rows: effectiveRowsData,
    columns,
    loading: false,
    disableRowSelectionOnClick: true,
    rowSelection: false,
    disableColumnMenu,
    disableColumnSorting: resolvedDisableColumnSorting,
    disableColumnResize: resolvedDisableColumnResize,
    showColumnVerticalBorder,
    showCellVerticalBorder,
    suppressFocusOutline: resolvedSuppressFocusOutline,
    hideFooterSelectedRowCount: true,
    pageSizeOptions: resolvedDisablePageSizeSelect ? [] : resolvedPageSizeOptions,
    paginationMode: 'server',
    filterMode: 'server',
    paginationModel,
    onPaginationModelChange: handlePaginationModelChange,
    rowCount: effectiveRowCount,
    ...sortingProps,
    onColumnHeaderClick: handleColumnHeaderClick,
    filterModel,
    onFilterModelChange: handleFilterModelChange,
    columnVisibilityModel,
    onColumnVisibilityModelChange: (...args) => {
      const [model, details] = args as Parameters<
        NonNullable<DataGridProps['onColumnVisibilityModelChange']>
      >;
      if (isColumnVisibilityModelEqual(columnVisibilityModel, model)) return;
      setColumnVisibilityModel((previousModel) =>
        isColumnVisibilityModelEqual(previousModel, model) ? previousModel : model,
      );
      gridOnColumnVisibilityModelChange?.(model, details);
    },
    onColumnWidthChange: handleColumnWidthChange,
    onRowClick,
    onCellClick,
    getRowId,
    autoHeight,
    columnHeaderHeight: gridColumnHeaderHeight ?? TABLE_GRID_HEADER_HEIGHT,
    rowHeight: resolvedRowHeight,
    getEstimatedRowHeight: gridGetEstimatedRowHeight,
    getRowHeight: resolvedGetRowHeight,
    getRowSpacing: zeroRowSpacing,
    rowSpacingType: 'margin',
    showToolbar,
    hideFooter: resolvedHideFooter,
    hidePaginationControls,
    sx: resolvedDataGridSx,
    emptyTitle: effectiveErrorMessage ?? DATA_GRID_EMPTY_STATE_NO_DATA_LABEL,
    error: Boolean(effectiveErrorMessage),
    errorTitle: effectiveErrorMessage ?? undefined,
    overlayHeight: emptyOverlayHeight,
    slotProps: {
      ...gridSlotProps,
    },
    ...restGridProps,
  };
};
